import React, { useEffect, useState } from 'react';
import { SpinResult } from '../../../types/games';
import { PieWheel, DEFAULT_SLICE_COLORS, DEFAULT_SLICE_TEXT_COLORS, WheelSlice } from '../PieWheel';
import { PrimaryButton, SecondaryButton, Pill, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';
import GameTodayResultCard from '../GameTodayResultCard';

interface SpinWheelGameProps {
  appName: string;
  outletId: number;
  tableLabel?: string;
  onClose?: () => void;
}

type Stage = 'loading' | 'idle' | 'locked' | 'spinning' | 'result';

const SpinWheelGame: React.FC<SpinWheelGameProps> = ({
  appName,
  outletId,
  tableLabel = 'Today',
  onClose,
}) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'SPIN_WHEEL');
  const [stage, setStage] = useState<Stage>('loading');
  const [result, setResult] = useState<SpinResult | null>(null);
  const [targetIndex, setTargetIndex] = useState<number | null>(null);
  const [spinToken, setSpinToken] = useState(0);

  useEffect(() => {
    if (loading) {
      setStage('loading');
      return;
    }
    setStage(eligible ? 'idle' : 'locked');
  }, [loading, eligible]);

  const slices: WheelSlice[] =
    eligibility?.segments?.map((s, i) => ({
      label: s.label,
      color: DEFAULT_SLICE_COLORS[i % DEFAULT_SLICE_COLORS.length],
      textColor: DEFAULT_SLICE_TEXT_COLORS[i % DEFAULT_SLICE_TEXT_COLORS.length],
    })) ?? [];

  async function handleSpin() {
    if (!eligibility?.segments) return;
    setStage('spinning');
    const idempotencyKey = gamesService.dailyIdempotencyKey(outletId, 'SPIN_WHEEL');
    try {
      const res = await gamesService.spin(appName, 'SPIN_WHEEL', idempotencyKey);
      const idx = eligibility.segments.findIndex((s) => s.label === res.resultSegment);
      setResult(res);
      setTargetIndex(idx >= 0 ? idx : 0);
      setSpinToken((t) => t + 1);
    } catch {
      setStage('locked');
    }
  }

  const bg: React.CSSProperties = { background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 };

  return (
    <div style={bg}>
      <ScreenHeader
        title={
          stage === 'result' ? (
            <>🎉 You <span style={{ color: 'var(--primary, #F5C21A)' }}>Won!</span></>
          ) : stage === 'locked' && eligibility?.todayResult?.isWin ? (
            <>Today&apos;s <span style={{ color: 'var(--primary, #F5C21A)' }}>Win</span></>
          ) : (
            <>Spin to <span style={{ color: 'var(--primary, #F5C21A)' }}>Win!</span></>
          )
        }
        subtitle={
          stage === 'result'
            ? 'The wheel has spoken'
            : stage === 'locked'
              ? 'One spin per day · result below'
              : `One free spin today · ${tableLabel}`
        }
        onClose={onClose}
      />

      <div style={{ padding: '20px 24px 0' }}>
        {stage === 'loading' && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 60 }}>Loading…</p>
        )}

        {(stage === 'idle' || stage === 'spinning' || (stage === 'result' && targetIndex !== null)) && slices.length > 0 && (
          <div style={{ margin: '18px 0 22px' }}>
            <PieWheel
              slices={slices}
              targetIndex={targetIndex}
              spinToken={spinToken}
              onSpinComplete={() => setStage('result')}
            />
          </div>
        )}

        {stage === 'idle' && <PrimaryButton onClick={handleSpin}>SPIN THE WHEEL</PrimaryButton>}
        {stage === 'spinning' && <PrimaryButton disabled>SPINNING…</PrimaryButton>}

        {stage === 'locked' && <GameLockedCard eligibility={eligibility} />}

        {stage === 'result' && result && (
          <>
            <GameTodayResultCard
              result={{
                resultSegment: result.resultSegment,
                redemptionCode: result.redemptionCode,
                expiresAt: result.expiresAt,
                isWin: Boolean(result.redemptionCode),
              }}
            />
            <div style={{ height: 12 }} />
            <PrimaryButton>SHOW TO WAITER</PrimaryButton>
            <div style={{ height: 10 }} />
            <SecondaryButton>Share the win 🎊</SecondaryButton>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <Pill tone="muted">Powered by Peppar</Pill>
        </div>
      </div>
    </div>
  );
};

export default SpinWheelGame;
